import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { retornoDe, type AlvoDoRetorno } from '@/lib/retorno';

import { api, ApiIndisponivel, RecusaDaApi, SemSessao, type DocumentoDaApi, type EnvioDeAplicacao, type EnvioDeDocumento } from './api';

/**
 * O módulo 12 falando em domínio.
 *
 * A chave é uma só (`['tecnico']`) e toda escrita a invalida inteira, de
 * propósito: validar um documento muda a ORDEM da lista, e revogar tira um
 * documento dela. Invalidar só a linha alterada deixaria a ordem velha na tela,
 * com a linha nova no lugar errado — e ninguém entende por que o primeiro item
 * não é o primeiro.
 */
const CHAVE = ['tecnico'] as const;

export function useDocumentos({ ligada = true }: { readonly ligada?: boolean } = {}) {
  return useQuery({
    queryKey: [...CHAVE, 'documentos'],
    /* A busca da barra superior so pergunta quando o perfil le a biblioteca. */
    enabled: ligada,
    queryFn: async () => (await api.documentos()).documentos,
    /* Enquanto algum manual esta sendo lido pela IA, a lista se atualiza
       sozinha: a leitura roda no servidor e avisa pelo andamento. */
    refetchInterval: (consulta) => (consulta.state.data?.some((d) => lendoAgora(d)) ? 3_000 : false),
  });
}

/**
 * Espelha `LEITURA_PARADA_MIN` do servidor (backend/src/biblioteca/trabalhos.ts):
 * a API que reinicia no meio deixa a leitura "lendo" para sempre, e a rota
 * aceita pedir de novo passado este prazo sem marca nova.
 */
const LEITURA_PARADA_MS = 20 * 60 * 1000;

/** A leitura pela IA está andando de fato — e não parada por uma API que reiniciou. */
export function lendoAgora(d: Pick<DocumentoDaApi, 'leituraIa' | 'leituraIaEm'>): boolean {
  if (d.leituraIa !== 'lendo') return false;
  const marca = d.leituraIaEm ? Date.parse(d.leituraIaEm) : Number.NaN;
  return Number.isFinite(marca) && Date.now() - marca < LEITURA_PARADA_MS;
}

/**
 * O manual como a lista o mostra — o "em quê" do retorno (R04, p.32). A
 * resposta de várias escritas traz só o id; o título vem do que a tela já leu.
 */
function useDocumentoEmCache(): (id: string) => AlvoDoRetorno | undefined {
  const cliente = useQueryClient();
  return (id) => {
    const d = cliente.getQueryData<readonly DocumentoDaApi[]>([...CHAVE, 'documentos'])?.find((x) => x.id === id);
    return d ? { nome: d.titulo, ...(d.revisao ? { codigo: `rev. ${d.revisao}` } : {}) } : undefined;
  };
}

const comAlvo = (alvo: AlvoDoRetorno | undefined) => (alvo ? { alvo } : {});

export function useIndice() {
  return useQuery({
    queryKey: [...CHAVE, 'indice'],
    queryFn: () => api.indice(),
    refetchInterval: (consulta) => {
      const i = consulta.state.data;
      return i?.ligado && i.pendentes > 0 ? 5_000 : false;
    },
  });
}

export function useLerPelaIa() {
  const cliente = useQueryClient();
  const documento = useDocumentoEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof api.lerPelaIa>>, string>((r, id) => ({
        titulo: 'Leitura pela IA iniciada',
        ...comAlvo(documento(id)),
        fatos: [`${String(r.paginas)} ${r.paginas === 1 ? 'página' : 'páginas'}`],
        efeito: 'A lista mostra o andamento; as respostas usam o manual quando a leitura terminar',
      })),
    },
    mutationFn: (id: string) => api.lerPelaIa(id),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useCompletarIndice() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Índice da biblioteca em andamento',
        alvo: { nome: 'Biblioteca técnica' },
        efeito: 'Os trechos que faltam ganham o vetor de busca; a contagem se atualiza sozinha',
      })),
    },
    mutationFn: () => api.completarIndice(),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useAlvos() {
  return useQuery({
    queryKey: [...CHAVE, 'alvos'],
    queryFn: () => api.alvos(),
  });
}

export function useGravarDocumento() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<DocumentoDaApi, { id?: string }>((d, v) => ({
        titulo: v.id === undefined ? 'Documento cadastrado' : 'Documento alterado',
        alvo: { nome: d.titulo, ...(d.revisao ? { codigo: `rev. ${d.revisao}` } : {}) },
        fatos: [d.tipo, d.situacao === 'validado' ? 'validado' : 'pendente de validação'],
      })),
    },
    mutationFn: ({ id, envio }: { id?: string; envio: EnvioDeDocumento }) =>
      id === undefined ? api.criar(envio) : api.editar(id, envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useValidarDocumento() {
  const cliente = useQueryClient();
  const documento = useDocumentoEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof api.validar>>, { id: string; motivo?: string }>((r, v) => ({
        titulo: 'Documento validado',
        ...comAlvo(documento(v.id)),
        fatos: [v.motivo ? `motivo: ${v.motivo}` : null],
        efeito: `Passou a ser fonte de nível ${String(r.prioridadeFonte)} nas respostas`,
      })),
    },
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) => api.validar(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useRevogarDocumento() {
  const cliente = useQueryClient();
  const documento = useDocumentoEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof api.revogar>>, { id: string; motivo: string }>((r, v) => ({
        titulo: 'Documento revogado',
        ...comAlvo(documento(v.id)),
        fatos: [`motivo: ${v.motivo}`],
        ...(r.saiuDasRespostas ? { efeito: 'Saiu das respostas da IA' } : {}),
      })),
    },
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => api.revogar(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useAplicar() {
  const cliente = useQueryClient();
  const documento = useDocumentoEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { id: string; envio: EnvioDeAplicacao }>((_r, v) => ({
        titulo: 'Aplicação do documento gravada',
        ...comAlvo(documento(v.id)),
        fatos: [
          v.envio.serieDe || v.envio.serieAte ? `série ${v.envio.serieDe ?? '…'} a ${v.envio.serieAte ?? '…'}` : null,
          v.envio.motorModelo ? `motor ${v.envio.motorModelo}` : null,
          v.envio.componente ?? null,
        ],
      })),
    },
    mutationFn: ({ id, envio }: { id: string; envio: EnvioDeAplicacao }) => api.aplicar(id, envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useDesaplicar() {
  const cliente = useQueryClient();
  const documento = useDocumentoEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { documentoId: string }>((_r, v) => ({
        titulo: 'Aplicação do documento removida',
        ...comAlvo(documento(v.documentoId)),
      })),
    },
    mutationFn: ({ documentoId, aplicacaoId }: { documentoId: string; aplicacaoId: string }) =>
      api.desaplicar(documentoId, aplicacaoId),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useEnviarArquivo() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<DocumentoDaApi, { arquivo: File }>((d, v) => ({
        titulo: 'Arquivo do documento enviado',
        alvo: { nome: d.titulo, ...(d.revisao ? { codigo: `rev. ${d.revisao}` } : {}) },
        fatos: [v.arquivo.name, d.paginas === null ? null : `${String(d.paginas)} ${d.paginas === 1 ? 'página' : 'páginas'}`],
      })),
    },
    mutationFn: ({ id, arquivo }: { id: string; arquivo: File }) => api.enviarArquivo(id, arquivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/**
 * Abre o arquivo numa aba nova.
 *
 * A aba é aberta ANTES de pedir a URL, e só depois recebe o endereço: o
 * navegador bloqueia janela aberta depois de uma espera, porque ela deixou de
 * ser resposta direta ao clique. Sem isso, o botão pareceria não fazer nada.
 */
export async function abrirArquivo(id: string): Promise<void> {
  const aba = window.open('about:blank', '_blank');
  try {
    const { url } = await api.urlDoArquivo(id);
    if (aba) {
      aba.opener = null;
      aba.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (erro) {
    aba?.close();
    throw erro;
  }
}

/**
 * A falha virada frase que diz o que fazer.
 *
 * `SemSessao` tem saída própria porque a genérica ("tente de novo") nunca
 * funcionaria: o que falta é entrar outra vez, e o formulário continua
 * preenchido enquanto isso.
 */
export function explicarFalha(erro: unknown): { readonly erro: string; readonly comoResolver: string } {
  if (erro instanceof SemSessao) {
    return {
      erro: 'sua sessão expirou',
      comoResolver: 'Entre outra vez. O que você digitou continua na tela.',
    };
  }
  if (erro instanceof ApiIndisponivel) {
    return {
      erro: 'o sistema não respondeu',
      comoResolver: 'Confira a conexão e tente de novo; se continuar, avise o suporte com o horário.',
    };
  }
  if (erro instanceof RecusaDaApi) {
    return { erro: erro.message, comoResolver: erro.comoResolver };
  }
  return {
    erro: 'não foi possível falar com o servidor',
    comoResolver: 'Tente de novo; se continuar, avise o suporte com o horário em que aconteceu.',
  };
}
